import { motion } from "framer-motion";
import { Monitor, Bell, Smartphone, ArrowRight } from "lucide-react";

const steps = [
  {
    icon: Monitor,
    label: "Mac Session",
    description: "Agent runs on your desktop",
  },
  {
    icon: Bell,
    label: "Notification",
    description: "Something needs attention",
  },
  {
    icon: Smartphone,
    label: "Phone Control",
    description: "Approve, stop, or reply",
  },
];

export default function SolutionSection() {
  return (
    <section
      data-testid="solution-section"
      className="py-24 px-6"
      style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <span
            className="text-xs uppercase tracking-wider mb-4 block"
            style={{
              color: "#10B981",
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "11px",
            }}
          >
            The solution
          </span>
          <h2
            className="text-3xl sm:text-4xl font-semibold tracking-tight text-white leading-tight"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Runzr turns your terminal into a persistent, mobile-aware workspace.
          </h2>
          <p className="text-base mt-4" style={{ color: "#A1A1AA" }}>
            Keep your coding sessions running. Get notified when it matters.
            Respond from anywhere.
          </p>
        </motion.div>

        {/* Flow visualization */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-0">
          {steps.map((step, i) => (
            <motion.div
              key={step.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.15 }}
              className="flex items-center gap-4 sm:gap-0"
            >
              <div
                className="flex flex-col items-center text-center px-8 py-6"
              >
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <step.icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                </div>
                <span
                  className="text-sm font-medium text-white mb-1"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {step.label}
                </span>
                <span className="text-xs" style={{ color: "#71717A" }}>
                  {step.description}
                </span>
              </div>
              {i < steps.length - 1 && (
                <ArrowRight
                  className="hidden sm:block w-5 h-5 flex-shrink-0 mx-2"
                  style={{ color: "#71717A" }}
                  strokeWidth={1.5}
                />
              )}
            </motion.div>
          ))}
        </div>

        {/* Capabilities row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-16 flex flex-wrap justify-center gap-3"
        >
          {[
            "Persistent sessions",
            "Mobile supervision",
            "Push notifications",
            "Structured approvals",
            "Live output view",
            "Fast intervention",
          ].map((cap) => (
            <span
              key={cap}
              className="text-xs px-3 py-1.5 rounded-full"
              style={{
                color: "#A1A1AA",
                border: "1px solid rgba(255,255,255,0.08)",
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "11px",
              }}
            >
              {cap}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
